import { inject, Injectable } from '@angular/core';
import { EnvironmentService } from '../environment.service';
import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from '@apollo/client';
import {
  FacilitySignIn,
  FacilitySignInInput,
  PublicFacility,
} from '../../model/locations/facility-sign-in';
import { from, map, Observable, of, switchMap } from 'rxjs';
import { FacilityBuilderService } from '../builder/facility-builder.service';
import { getPublicFacilities } from '../../graphql/facilities/get-public-facilites';
import { SetContextLink } from '@apollo/client/link/context';
import { createFacilitySignIn } from '../../graphql/facilities/create-facility-sign-in';
import { getSignedInPeople } from 'src/app/graphql/facilities/get-signed-in-people';
import { createFacilitySignOutFromPhone } from 'src/app/graphql/facilities/create-facility-sign-out-from-phone';
import { getGraphQLField } from '../../graphql/graphql-response';

@Injectable({
  providedIn: 'root',
})
export class PublicFacilityApiService {
  private _env = inject(EnvironmentService);
  private _facilityBuilder = inject(FacilityBuilderService);

  client!: ApolloClient;
  cache = new InMemoryCache();
  apiClient!: Observable<ApolloClient>;

  constructor() {
    this.configure();
  }

  async configure() {
    const url = this._env.getGraphQLURL();

    // API Key used for Public API rather than using the token.
    const authLink = new SetContextLink(async ({ headers }) => {
      return {
        headers: {
          ...headers,
          'x-api-key': this._env.getpublicFunctionApiKey(),
        },
      };
    });

    const httpLink = new HttpLink({ uri: url });

    this.client = new ApolloClient({
      link: ApolloLink.from([authLink, httpLink]),
      cache: this.cache,
    });
  }

  getObservedClient(): Observable<ApolloClient> {
    return of(this.client);
  }

  getPublicFacilities(): Observable<PublicFacility[]> {
    return from(this.getObservedClient())
      .pipe(
        switchMap((client) => {
          return client.query<Record<string, PublicFacility[]>>({
            query: getPublicFacilities,
            variables: {},
            fetchPolicy: 'no-cache',
          });
        })
      )
      .pipe(
        map((result) => {
          return getGraphQLField(result.data, 'getPublicFacilities').map((i) =>
            this._facilityBuilder.buildPublicFacility(i)
          );
        })
      );
  }

  createFacilitySignIn(
    id: string | null,
    input: FacilitySignInInput | null
  ): Observable<FacilitySignIn> {
    return from(this.getObservedClient())
      .pipe(
        switchMap((client) => {
          return client.mutate<Record<string, unknown>>({
            mutation: createFacilitySignIn,
            variables: { id, input },
          });
        })
      )
      .pipe(
        map((result) => {
          return this._facilityBuilder.buildFacilitySignIn(
            getGraphQLField(result.data, 'createFacilitySignIn')
          );
        })
      );
  }

  createFacilitySignOutFromPhone(
    facilityId: string,
    phoneNumber: string
  ): Observable<any> {
    return from(this.getObservedClient()).pipe(
      switchMap((client) => {
        return client.mutate({
          mutation: createFacilitySignOutFromPhone,
          variables: { facilityId, phoneNumber },
        });
      })
    );
  }
}
